import { useState } from 'react';
import Modal from "../../common/Modal"
import { FaFilePdf, FaTimes, FaFile } from "react-icons/fa"
import PdfPreviewPanel from './PdfPreviewPanel';

export const getUnitMeasurement = (code) => {
  const unitMeasurements = {
    'H87': 'bucată',
    'SET': 'set',
    'HUR': 'ora',
    'KGM': 'kilogram',
    'GRM': 'gram',
    'LTR': 'litru',
    'MTR': 'metru'
  };

  return unitMeasurements[code] || code;
};

export default function ViewNIR({ grn, onClose, isOpen, inventories = [] }) {
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  
  // Calculate total value
  const totalValue = grn?.items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0;

  // Function to get inventory name from ID
  const getInventoryName = (inventoryId) => {
    if (!inventoryId) return "-";
    const inventory = inventories.find(inv => inv.id === inventoryId);
    return inventory ? inventory.name : inventoryId;
  };
  const onViewDocument = () => {
    if (grn && grn.id) {
      window.open(`/print-nir?id=${grn.id}`, '_blank');
    }
  }

  // Function to convert unit measurement codes to their descriptions

  if (!isOpen || !grn) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Notă de Recepție"
      panelClassName={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${showPdfPreview ? 'max-w-[95vw]' : 'max-w-6xl'} max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col`}
      header={(
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FaFile className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Notă de Recepție: {grn.seriesNumber}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Vizualizați detaliile complete ale documentului
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowPdfPreview(!showPdfPreview)}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 rounded-lg transition-colors ${
                  showPdfPreview
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                }`}
                title={showPdfPreview ? "Ascunde PDF" : "Previzualizare PDF"}
              >
                <FaFilePdf className="w-3 h-3" />
                {showPdfPreview ? 'Ascunde PDF' : 'PDF'}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      content={(
        <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 14rem)' }}>
          {/* Main Content */}
          <div className={`flex-1 overflow-y-auto p-3 sm:p-6 ${showPdfPreview ? 'w-1/2' : 'w-full'}`}>
            {/* Main Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6 sm:mb-8">
              <div>
                <h4 className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-3 tracking-wider">Informații document</h4>
                <div className="space-y-3 bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Serie și număr document</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{grn.seriesNumber || "-"}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Data</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                      {new Date(grn.date).toLocaleDateString("ro-RO")}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Reprezentant</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                      {grn.representative?.name || "-"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-3 tracking-wider">Informații suplimentare</h4>
                <div className="space-y-3 bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Inventar</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                      {grn.inventoryName || getInventoryName(grn.inventoryId)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Nume furnizor</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                      {grn.shop?.name || grn.vendor?.name || "-"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Număr document furnizor</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                      {grn.invoiceReceivedNr || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6 sm:mb-8">
              <h4 className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-3 tracking-wider">Produse recepționate</h4>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nr.</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cod</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Produs</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cantitate</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">UM</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preț unitar</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Adaos</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Preț vânzare</th>
                        <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {grn.items && grn.items.length > 0 ? (
                        grn.items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">{idx + 1}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">{item.productIdentification || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-900 dark:text-gray-100 font-medium">{item.productName || item.productId || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">{item.quantityReceived || "-"}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">{getUnitMeasurement(item.um)}</td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">
                              {item.unitPrice ? `${item.unitPrice.toFixed(2)} RON` : "-"}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">
                              {item.markup ? `${(item.markup * 100).toFixed(0)}%` : "-"}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-700 dark:text-gray-300">
                              {item.sellingPrice ? `${item.sellingPrice.toFixed(2)} RON` : "-"}
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-purple-600 dark:text-purple-400 font-medium">
                              {item.total ? `${item.total.toFixed(2)} RON` : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="py-4 text-center text-gray-500 dark:text-gray-400 italic">
                            Nu există produse în această notă de recepție.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <td colSpan="8" className="py-2 sm:py-3 px-2 sm:px-4 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                          Total:
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm font-bold text-purple-700 dark:text-purple-300">
                          {totalValue.toFixed(2)} RON
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                {/* Mobile scroll hint */}
                <div className="sm:hidden bg-gray-50 dark:bg-gray-800 px-3 py-2 text-center border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">← Glisați pentru a vedea toate coloanele →</p>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            {grn.notes && (
              <div className="mb-6 sm:mb-8">
                <h4 className="text-xs uppercase font-bold text-gray-500 dark:text-gray-400 mb-3 tracking-wider">Note</h4>
                <div className="bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-sm sm:text-base">{grn.notes}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* PDF Preview Panel */}
          {showPdfPreview && (
            <div className="w-1/2 h-full">
              <PdfPreviewPanel
                endpoint="/grn/{id}/pdf"
                documentId={grn.id}
                title="Previzualizare NIR"
                isVisible={showPdfPreview}
                onClose={() => setShowPdfPreview(false)}
                downloadFileName={`nir-${grn.seriesNumber || grn.id}.pdf`}
              />
            </div>
          )}
        </div>
      )}
      footer={(
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 rounded-b-lg">
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:gap-2">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              Închide
            </button>
          </div>
        </div>
      )}
    />
  );
}
